import { Link } from '@/components/ui/link';
import { Title } from '@/components/ui/layout';
import { ArrowRight, HandHeart } from 'lucide-react';
import { useState } from 'react';

import { useLanguage } from '@/i18n/language-context';
import type { ProjectCatalog } from '@/api/public-organizations';
import { useAnalytics } from '@/lib/use-analytics';
import './project-card.css';

export const OrganizationProjectCard = ({
  project,
  showOwner = true,
  headingLevel = 4,
}: {
  project: ProjectCatalog['items'][number];
  showOwner?: boolean;
  headingLevel?: 3 | 4;
}): JSX.Element => {
  const { t } = useLanguage();
  const track = useAnalytics();
  const [failedImage, setFailedImage] = useState<string | null>(null);
  const href = `/p/${encodeURIComponent(project.slug)}`;
  const imageUrl = project.coverImageUrl;
  const showImage = Boolean(imageUrl && imageUrl !== failedImage);

  return (
    <Link
      appearance="bare"
      className="organization-project-card project-card"
      to={href}
      aria-label={project.title}
      onClick={() =>
        track('Public Project Selected', {
          project_id: project.id,
          organization_id: project.organizationId,
          placement: 'project_card',
        })
      }
    >
      <div className="organization-project-card__image project-card__image">
        {showImage ? (
          <img
            src={imageUrl ?? undefined}
            alt={project.coverImageAlt?.trim() || project.title}
            loading="lazy"
            decoding="async"
            onError={() => setFailedImage(imageUrl)}
          />
        ) : (
          <div
            className="organization-project-card__placeholder project-card__placeholder"
            aria-hidden="true"
          >
            <HandHeart size={34} strokeWidth={1.4} />
          </div>
        )}
      </div>
      <div className="organization-project-card__body project-card__body">
        <Title level={headingLevel} className="project-card__title">
          {project.title}
        </Title>
        {showOwner && <p className="project-card__owner">{project.publicOrganization.name}</p>}
        {project.summary && <p className="project-card__summary">{project.summary}</p>}
        <span className="organization-card-action project-card__action">
          {t.viewProject} <ArrowRight size={16} aria-hidden="true" />
        </span>
      </div>
    </Link>
  );
};
